import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MapPin, Plus, Search, Users } from 'lucide-react';
import { useState } from 'react';
import type { Account, AccountAddress, AccountContact } from '@/api/commercial/accounts';
import { accountApi } from '@/api/commercial/accounts';
import type { CreditAnalysis } from '@/api/commercial/credit-analysis';
import { historicalApi } from '@/api/commercial/historicals';
import { AddressCard } from '@/components/commercial/accounts/create/address-card';
import { AddressFormDialog } from '@/components/commercial/accounts/create/address-form-dialog';
import { ContactCard } from '@/components/commercial/accounts/create/contact-card';
import { ContactFormDialog } from '@/components/commercial/accounts/create/contact-form-dialog';
import { AccountCard } from '@/components/commercial/accounts/details/account-card';
import { AccountDataUpdateDialog } from '@/components/commercial/accounts/details/account-data-update-dialog';
import { AccountRefreshDialog } from '@/components/commercial/accounts/details/account-refresh-dialog';
import { EconomicGroupCard } from '@/components/commercial/accounts/details/economic-group-card';
import {
  EconomicGroupUpdateDialog,
  type EconomicGroupUpdateResult,
} from '@/components/commercial/accounts/details/economic-group-update-dialog';
import { EconomicGroupManagementDialog } from '@/components/commercial/accounts/economic-group-management-dialog';
import { SearchContactDialog } from '@/components/commercial/accounts/search-contact-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/auth-context';
import type { LocalAccount, LocalAddress, LocalContact } from '@/context/create-account-flow';
import { useCanSearchGroupContacts } from '@/hooks/use-can-search-group-contacts';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-message';
import {
  toLocalAccount,
  toLocalAddress,
  toLocalContact,
  toLocalEconomicGroup,
} from '@/lib/mappers/account.mapper';
import { mergeFiscalContacts } from '@/lib/merge-fiscal-contacts';

export interface CreditAnalysisTabProps {
  creditAnalysis: CreditAnalysis;
}

export function CreditAnalysisGeneralTab({ creditAnalysis }: CreditAnalysisTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const accountId = creditAnalysis.accountId!;

  // Modal states
  const [isAccountDialogOpen, setIsAccountDialogOpen] = useState(false);
  const [isEconomicGroupDialogOpen, setIsEconomicGroupDialogOpen] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isAddressDialogOpen, setIsAddressDialogOpen] = useState(false);
  const [isDuplicateAddressDialogOpen, setIsDuplicateAddressDialogOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isRefreshDialogOpen, setIsRefreshDialogOpen] = useState(false);
  const [isSearchContactDialogOpen, setIsSearchContactDialogOpen] = useState(false);
  const [isManageGroupsDialogOpen, setIsManageGroupsDialogOpen] = useState(false);

  // Edit states
  const [editingContact, setEditingContact] = useState<LocalContact | null>(null);
  const [duplicatingContact, setDuplicatingContact] = useState<LocalContact | null>(null);
  const [isDuplicateContactDialogOpen, setIsDuplicateContactDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<LocalAddress | null>(null);
  const [duplicatingAddress, setDuplicatingAddress] = useState<LocalAddress | null>(null);
  const [deletingItem, setDeletingItem] = useState<{
    type: 'contact' | 'address';
    id: string;
  } | null>(null);

  const [
    { data: account, isLoading: isLoadingAccount },
    { data: addresses = [], isLoading: isLoadingAddresses },
    { data: contacts = [], isLoading: isLoadingContacts },
  ] = useQueries({
    queries: [
      {
        queryKey: ['account', accountId],
        queryFn: () => accountApi.findOne(accountId),
        enabled: !!accountId,
      },
      {
        queryKey: ['account-addresses', accountId],
        queryFn: () => accountApi.findAddresses(accountId),
        enabled: !!accountId,
      },
      {
        queryKey: ['account-contacts', accountId],
        queryFn: () => accountApi.findContacts(accountId),
        enabled: !!accountId,
      },
    ],
  });

  const { data: economicGroup } = useQuery({
    queryKey: ['economic-group', account?.economicGroupId],
    queryFn: () => accountApi.findEconomicGroup(account?.economicGroupId!),
    enabled: !!account?.economicGroupId,
  });

  const canSearchGroupContacts = useCanSearchGroupContacts(
    account?.economicGroupId,
    account?.id,
    account?.document.type,
  );

  // Mutations
  const updateAccountMutation = useMutation({
    mutationFn: (data: Partial<Account>) => accountApi.update(accountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-history', accountId] });
      toast({ variant: 'success', title: 'Sucesso', description: 'Dados da conta atualizados.' });
      historicalApi
        .create({
          accountId,
          title: 'Dados da conta atualizados.',
          type: 'manual',
          author: { name: user?.name ?? '', avatarUrl: '' },
        })
        .catch(() => {});
    },
  });

  const createAddressMutation = useMutation({
    mutationFn: (data: Omit<AccountAddress, 'id'>) => accountApi.createAddress(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-addresses', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-history', accountId] });
      toast({ variant: 'success', title: 'Sucesso', description: 'Endereço adicionado.' });
      historicalApi
        .create({
          accountId,
          title: 'Endereço adicionado.',
          type: 'manual',
          author: { name: user?.name ?? '', avatarUrl: '' },
        })
        .catch(() => {});
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: ({ addressId, data }: { addressId: string; data: Partial<AccountAddress> }) =>
      accountApi.updateAddress(addressId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-addresses', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-history', accountId] });
      toast({ variant: 'success', title: 'Sucesso', description: 'Endereço atualizado.' });
      historicalApi
        .create({
          accountId,
          title: 'Endereço atualizado.',
          type: 'manual',
          author: { name: user?.name ?? '', avatarUrl: '' },
        })
        .catch(() => {});
    },
  });

  const deleteAddressMutation = useMutation({
    mutationFn: (addressId: string) => accountApi.deleteAddress(addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-addresses', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-history', accountId] });
      toast({ variant: 'success', title: 'Sucesso', description: 'Endereço removido.' });
      historicalApi
        .create({
          accountId,
          title: 'Endereço removido.',
          type: 'manual',
          author: { name: user?.name ?? '', avatarUrl: '' },
        })
        .catch(() => {});
    },
    onError: () => {
      toast({ title: 'Erro', description: 'Falha ao remover endereço.', variant: 'destructive' });
    },
  });

  const createContactMutation = useMutation({
    mutationFn: (data: Omit<AccountContact, 'id'>) => accountApi.createContact(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-contacts', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-history', accountId] });
      toast({ variant: 'success', title: 'Sucesso', description: 'Contato adicionado.' });
      historicalApi
        .create({
          accountId,
          title: 'Contato adicionado.',
          type: 'manual',
          author: { name: user?.name ?? '', avatarUrl: '' },
        })
        .catch(() => {});
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: ({ contactId, data }: { contactId: string; data: Partial<AccountContact> }) =>
      accountApi.updateContact(contactId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-contacts', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-history', accountId] });
      toast({ variant: 'success', title: 'Sucesso', description: 'Contato atualizado.' });
      historicalApi
        .create({
          accountId,
          title: 'Contato atualizado.',
          type: 'manual',
          author: { name: user?.name ?? '', avatarUrl: '' },
        })
        .catch(() => {});
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: (contactId: string) => accountApi.deleteContact(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-contacts', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-history', accountId] });
      toast({ variant: 'success', title: 'Sucesso', description: 'Contato removido.' });
      historicalApi
        .create({
          accountId,
          title: 'Contato removido.',
          type: 'manual',
          author: { name: user?.name ?? '', avatarUrl: '' },
        })
        .catch(() => {});
    },
    onError: () => {
      toast({ title: 'Erro', description: 'Falha ao remover contato.', variant: 'destructive' });
    },
  });

  // Handlers
  const handleAccountUpdate = async (data: Partial<LocalAccount>) => {
    await updateAccountMutation.mutateAsync(data as Partial<Account>);
  };

  const handleEconomicGroupUpdate = async (data: EconomicGroupUpdateResult) => {
    const oldGroupId = account?.economicGroupId;
    const newGroupId = data.selectedGroupId;

    try {
      await accountApi.updateEconomicGroupVinculation(accountId, newGroupId || null);

      queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-history', accountId] });
      if (oldGroupId) queryClient.invalidateQueries({ queryKey: ['economic-group', oldGroupId] });
      if (newGroupId) queryClient.invalidateQueries({ queryKey: ['economic-group', newGroupId] });

      toast({ variant: 'success', title: 'Sucesso', description: 'Grupo econômico atualizado.' });
      historicalApi
        .create({
          accountId,
          title: 'Grupo econômico atualizado.',
          type: 'manual',
          author: { name: user?.name ?? '', avatarUrl: '' },
        })
        .catch(() => {});
    } catch (error) {
      console.error('Error updating economic group:', error);
      throw error;
    }
  };

  const handleContactSubmit = async (data: LocalContact) => {
    if (editingContact) {
      await updateContactMutation.mutateAsync({
        contactId: data.id,
        data,
      });
    } else {
      await createContactMutation.mutateAsync({
        ...data,
        accountId,
      });
    }
    setEditingContact(null);
  };

  const handleAddressSubmit = async (data: LocalAddress) => {
    if (editingAddress) {
      await updateAddressMutation.mutateAsync({
        addressId: data.id,
        data,
      });
    } else {
      await createAddressMutation.mutateAsync({
        ...data,
        accountId,
      });
    }
    setEditingAddress(null);
  };

  const handleDuplicateContactSubmit = async (data: LocalContact) => {
    await createContactMutation.mutateAsync({
      ...data,
      accountId,
    });
    setDuplicatingContact(null);
  };

  const handleDuplicateAddressSubmit = async (data: LocalAddress) => {
    await createAddressMutation.mutateAsync({
      ...data,
      accountId,
    });
    setDuplicatingAddress(null);
  };

  const handleDeleteConfirm = () => {
    if (!deletingItem) return;

    if (deletingItem.type === 'contact') {
      deleteContactMutation.mutate(deletingItem.id);
    } else {
      deleteAddressMutation.mutate(deletingItem.id);
    }

    setDeletingItem(null);
    setIsDeleteAlertOpen(false);
  };

  const openContactEdit = (contact: LocalContact) => {
    setEditingContact(contact);
    setIsContactDialogOpen(true);
  };

  const openContactCreate = () => {
    setEditingContact(null);
    setIsContactDialogOpen(true);
  };

  const openContactDuplicate = (contact: LocalContact) => {
    setDuplicatingContact(contact);
    setIsDuplicateContactDialogOpen(true);
  };

  const openAddressEdit = (address: LocalAddress) => {
    setEditingAddress(address);
    setIsAddressDialogOpen(true);
  };

  const openAddressCreate = () => {
    setEditingAddress(null);
    setIsAddressDialogOpen(true);
  };

  const openAddressDuplicate = (address: LocalAddress) => {
    setDuplicatingAddress(address);
    setIsDuplicateAddressDialogOpen(true);
  };

  const openDeleteAlert = (type: 'contact' | 'address', itemId: string) => {
    setDeletingItem({ type, id: itemId });
    setIsDeleteAlertOpen(true);
  };

  const handleRefreshAccount = () => {
    setIsRefreshDialogOpen(true);
  };

  const handleRefreshConfirm = (data: Partial<LocalAccount>) => {
    updateAccountMutation.mutate(data as Partial<Account>, {
      onError: (error) =>
        toast({ title: 'Erro', description: getErrorMessage(error), variant: 'destructive' }),
    });
  };

  if (isLoadingAccount) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!account) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Conta não encontrada</p>
        </CardContent>
      </Card>
    );
  }

  const localAccount = toLocalAccount(account);
  const localAddresses = addresses.map(toLocalAddress);
  const localContacts = contacts.map(toLocalContact);
  const mergedContacts = mergeFiscalContacts(localContacts);
  const localEconomicGroup = toLocalEconomicGroup(economicGroup || null);

  return (
    <>
      <div className="space-y-6">
        {/* Account and Economic Group Cards */}
        <div
          className={`grid grid-cols-1 gap-6 ${account.document.type === 'cnpj' ? 'lg:grid-cols-2' : ''}`}
        >
          <AccountCard
            account={localAccount}
            billingStatus={account.billing_status}
            billingSituation={account.billing_situation}
            onEdit={() => setIsAccountDialogOpen(true)}
            onRefresh={handleRefreshAccount}
          />
          {account.document.type === 'cnpj' && (
            <EconomicGroupCard
              economicGroup={localEconomicGroup}
              currentAccountTaxId={account.document.value}
              onManageGroups={() => setIsManageGroupsDialogOpen(true)}
            />
          )}
        </div>

        {/* Contacts Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg">Contatos</CardTitle>
                  <Badge variant="secondary">{localContacts.length}</Badge>
                </div>
              </div>
              <div className="flex gap-2">
                {canSearchGroupContacts && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsSearchContactDialogOpen(true)}
                  >
                    <Search className="h-4 w-4 mr-1" />
                    Buscar Contato
                  </Button>
                )}
                <Button onClick={openContactCreate} size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingContacts ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : localContacts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Users className="h-10 w-10 text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">Nenhum contato cadastrado</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={openContactCreate}>
                  Adicionar contato
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-4">
                {mergedContacts.map((contact) => {
                  const isFiscalContact = contact.contactFor?.includes('Fiscal') ?? false;

                  return (
                    <ContactCard
                      key={contact.id}
                      contact={contact}
                      onEdit={() => openContactEdit(contact)}
                      onDelete={() => openDeleteAlert('contact', contact.id)}
                      onDuplicate={
                        isFiscalContact ? () => openContactDuplicate(contact) : undefined
                      }
                    />
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Addresses Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg">Endereços</CardTitle>
                  <Badge variant="secondary">{localAddresses.length}</Badge>
                </div>
              </div>
              <Button onClick={openAddressCreate} size="sm">
                <Plus className="h-4 w-4 mr-1" />
                Adicionar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingAddresses ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : localAddresses.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <MapPin className="h-10 w-10 text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">Nenhum endereço cadastrado</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={openAddressCreate}>
                  Adicionar endereço
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-4">
                {localAddresses.map((address) => {
                  const isFiscalApi = address.type?.includes('Fiscal') ?? false;

                  return (
                    <AddressCard
                      key={address.id}
                      title={address.type?.join(', ') || 'Endereço'}
                      address={address}
                      onEdit={isFiscalApi ? undefined : () => openAddressEdit(address)}
                      onDelete={
                        isFiscalApi ? undefined : () => openDeleteAlert('address', address.id)
                      }
                      onDuplicate={isFiscalApi ? () => openAddressDuplicate(address) : undefined}
                    />
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <AccountDataUpdateDialog
        open={isAccountDialogOpen}
        onClose={() => setIsAccountDialogOpen(false)}
        onSubmit={handleAccountUpdate}
        account={localAccount}
      />

      <EconomicGroupUpdateDialog
        open={isEconomicGroupDialogOpen}
        onClose={() => setIsEconomicGroupDialogOpen(false)}
        onSubmit={handleEconomicGroupUpdate}
        currentGroupId={account.economicGroupId}
        accountTaxId={account.document.value}
        accountName={account.social_name || account.fantasy_name || account.name || ''}
      />

      <EconomicGroupManagementDialog
        open={isManageGroupsDialogOpen}
        onOpenChange={setIsManageGroupsDialogOpen}
        onMutationSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['account', accountId] });
          queryClient.invalidateQueries({ queryKey: ['economic-group'] });
        }}
      />

      <ContactFormDialog
        open={isContactDialogOpen}
        onClose={() => {
          setIsContactDialogOpen(false);
          setEditingContact(null);
        }}
        onSubmit={handleContactSubmit}
        contact={editingContact || undefined}
        mode={editingContact ? 'edit' : 'create'}
      />

      <ContactFormDialog
        open={isDuplicateContactDialogOpen}
        onClose={() => {
          setIsDuplicateContactDialogOpen(false);
          setDuplicatingContact(null);
        }}
        onSubmit={handleDuplicateContactSubmit}
        contact={duplicatingContact || undefined}
        mode="create"
        duplicateMode
      />

      <AddressFormDialog
        open={isAddressDialogOpen}
        onClose={() => {
          setIsAddressDialogOpen(false);
          setEditingAddress(null);
        }}
        onSubmit={handleAddressSubmit}
        address={editingAddress || undefined}
        mode={editingAddress ? 'edit' : 'create'}
      />

      <AddressFormDialog
        open={isDuplicateAddressDialogOpen}
        onClose={() => {
          setIsDuplicateAddressDialogOpen(false);
          setDuplicatingAddress(null);
        }}
        onSubmit={handleDuplicateAddressSubmit}
        address={duplicatingAddress || undefined}
        defaultValues={
          duplicatingAddress
            ? {
                zip_code: duplicatingAddress.zip_code || '',
                street: duplicatingAddress.street || '',
                number: duplicatingAddress.number || '',
                complement: duplicatingAddress.complement || '',
                district: duplicatingAddress.district || '',
                city: duplicatingAddress.city || '',
                state: duplicatingAddress.state || '',
                reference_point: duplicatingAddress.reference_point || '',
                type: [],
              }
            : undefined
        }
        mode="create"
        duplicateMode
      />

      <AccountRefreshDialog
        open={isRefreshDialogOpen}
        onClose={() => setIsRefreshDialogOpen(false)}
        account={localAccount}
        onConfirm={handleRefreshConfirm}
      />

      {account?.economicGroupId && (
        <SearchContactDialog
          open={isSearchContactDialogOpen}
          onClose={() => setIsSearchContactDialogOpen(false)}
          economicGroupId={account.economicGroupId}
          currentAccountId={account.id}
          existingContacts={contacts}
          onImport={async (contacts) => {
            for (const contact of contacts) {
              await createContactMutation.mutateAsync(contact);
            }
          }}
        />
      )}

      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este{' '}
              {deletingItem?.type === 'contact' ? 'contato' : 'endereço'}? Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingItem(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
